import {defineMessages} from 'react-intl'

export {convertMilesToKm} from '../measurement'

interface Message {
  id: string
  defaultMessage: string
}

interface Props {
  placeCategory: number
  fromUserLocation?: boolean
  geoDetailsFeatureType?: string
  geoDetailsShortName?: string
}

const FROM_TARGET_CENTER_CATEGORIES = [3, 4, 5, 7]
const FROM_TARGET_CATEGORIES = [0, 6, 8]

/**
 * Internationalization messages for distance display
 */
export const messages = defineMessages({
  distanceAsMiles: {
    id: 'distanceAsMiles',
    defaultMessage: 'miles'
  },
  distanceAsKm: {
    id: 'distanceAsKm',
    defaultMessage: 'km'
  },
  distanceFromTarget: {
    id: 'distanceFromTarget',
    defaultMessage: '{distance} {unit} from {targetName}'
  },
  distanceFromTargetCenter: {
    id: 'distanceFromTown',
    defaultMessage: '{distance} {unit} from {targetName} center'
  },
  distanceFromYourLocation: {
    id: 'distanceFromYourLocation',
    defaultMessage: '{distance} {unit} from your location'
  }
})

/**
 * Returns a message descriptor indicating the distance message based on the place category or user's location.
 * @param placeCategory - Object containing place category information
 * @param placeCategory.fromUserLocation - Boolean indicating if distance is from user's location
 * @param placeCategory.geoDetailsFeatureType - String representing the type of the feature
 * @param placeCategory.geoDetailsShortName - String representing a shorter version of the address
 * @param placeCategory.placeCategory - Number representing the type of place (0,6,8=Airport; 3,5=Subcity; 4,7=Town-village)
 * @returns DistanceFromProps | null - Returns an object with id and defaultMessage based on the specified place category or
 *  `fromUserLocation` flag. Returns `null` if the place category does not match any known types.
 * @example
 * // Returns messages.distanceFromYourLocation
 * getDistanceFromMessage(4, true);
 *
 * // Returns null if placeCategory is not recognized
 * getDistanceFromMessage(-1);
 */
export const getDistanceFromMessage = ({
  fromUserLocation,
  geoDetailsFeatureType,
  geoDetailsShortName,
  placeCategory
}: Props): Message | null => {
  if (fromUserLocation) return messages.distanceFromYourLocation
  if (geoDetailsShortName) {
    return geoDetailsFeatureType === 'address'
      ? messages.distanceFromTarget
      : messages.distanceFromTargetCenter
  }

  if (FROM_TARGET_CATEGORIES.includes(placeCategory))
    return messages.distanceFromTarget

  if (FROM_TARGET_CENTER_CATEGORIES.includes(placeCategory))
    return messages.distanceFromTargetCenter

  return null
}
